// src/pages/ListasPage.js
// Sprint 1 E1.5 — tela /listas com 3 tabs: Inclusão | Anti-Listas | Suppression Rules.
//
// Tabelas (P0 v2.1):
//   - crm_lists (list_type ∈ static|dynamic|campaign|working|antilist; entity_type, scope, is_active)
//   - crm_list_memberships (list_id, entity_type, entity_id)
//   - crm_suppression_rules (slug, nome, rule_type, severity, implementation_status, scope, description)

import React, { useEffect, useState } from 'react';
import { ListChecks, Ban, Shield, Users as UsersIcon } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import { supabase } from '../supabaseClient';
import { useUserRole } from '../hooks/useUserRole';

export default function ListasPage() {
  const [activeTab, setActiveTab] = useState('inclusion');

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Listas" subtitle="Inclusão, Anti-Listas e Supressões" icon={ListChecks} />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3 max-w-6xl mx-auto">
        <div className="flex border-b border-slate-200 mb-4">
          <TabButton
            active={activeTab === 'inclusion'}
            onClick={() => setActiveTab('inclusion')}
            label="Listas de Inclusão"
            icon={ListChecks}
          />
          <TabButton
            active={activeTab === 'antilist'}
            onClick={() => setActiveTab('antilist')}
            label="Anti-Listas"
            icon={Ban}
          />
          <TabButton
            active={activeTab === 'rules'}
            onClick={() => setActiveTab('rules')}
            label="Suppression Rules"
            icon={Shield}
          />
        </div>

        {activeTab === 'inclusion' && <ListasInclusao />}
        {activeTab === 'antilist' && <ListasAntilist />}
        {activeTab === 'rules' && <SuppressionRulesList />}
      </main>
    </div>
  );
}

function TabButton({ active, onClick, label, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 -mb-px border-b-2 text-xs font-medium transition-colors flex items-center gap-1.5 ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-slate-600 hover:text-slate-900'
      }`}
    >
      {Icon && <Icon size={13} />} {label}
    </button>
  );
}

// ============================================================================
// LISTAS DE INCLUSÃO
// ============================================================================

function ListasInclusao() {
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('crm_lists')
      .select('*')
      .in('list_type', ['static', 'dynamic', 'campaign', 'working'])
      .eq('is_active', true)
      .order('updated_at', { ascending: false });
    setListas(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-xs text-slate-500">Carregando…</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-slate-600">{listas.length} lista(s)</p>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
        >
          + Nova lista
        </button>
      </div>

      {listas.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-500">Nenhuma lista criada ainda.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-3 text-blue-600 text-xs hover:underline"
          >
            Criar primeira lista
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {listas.map((lista) => (
            <ListaCard key={lista.id} lista={lista} />
          ))}
        </div>
      )}

      {showCreate && (
        <ListaCreateModal
          listType="static"
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ANTI-LISTAS
// ============================================================================

function ListasAntilist() {
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const { role } = useUserRole();
  const isFounder = role === 'founder' || role === 'admin';

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('crm_lists')
      .select('*')
      .eq('list_type', 'antilist')
      .eq('is_active', true)
      .order('updated_at', { ascending: false });
    setListas(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-xs text-slate-500">Carregando…</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-slate-600">{listas.length} anti-lista(s)</p>
        {isFounder ? (
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700"
          >
            + Nova anti-lista
          </button>
        ) : (
          <span className="text-[11px] text-slate-500 italic">
            Apenas founder pode criar anti-listas globais
          </span>
        )}
      </div>

      {listas.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-500">Nenhuma anti-lista cadastrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {listas.map((lista) => (
            <ListaCard key={lista.id} lista={lista} variant="antilist" />
          ))}
        </div>
      )}

      {showCreate && (
        <ListaCreateModal
          listType="antilist"
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// SUPPRESSION RULES
// ============================================================================

function SuppressionRulesList() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase
        .from('crm_suppression_rules')
        .select('*')
        .order('implementation_status')
        .order('severity')
        .order('slug');
      if (mounted) {
        setRules(data || []);
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <div className="text-xs text-slate-500">Carregando…</div>;

  return (
    <div>
      <p className="text-xs text-slate-600 mb-3">
        {rules.length} regra(s) configurada(s) — supressões aplicadas em outbound, intake e enriquecimento.
      </p>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Nome</th>
              <th className="px-3 py-2 text-left font-semibold">Tipo</th>
              <th className="px-3 py-2 text-left font-semibold">Severity</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Escopo</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-500 italic">
                  Nenhuma regra configurada.
                </td>
              </tr>
            )}
            {rules.map((rule) => (
              <tr key={rule.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 align-top">
                  <div className="font-medium text-slate-800">{rule.nome}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{rule.description}</div>
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">{rule.slug}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <Badge variant="gray">{rule.rule_type}</Badge>
                </td>
                <td className="px-3 py-2 align-top">
                  <SeverityBadge severity={rule.severity} />
                </td>
                <td className="px-3 py-2 align-top">
                  <StatusBadge status={rule.implementation_status} />
                </td>
                <td className="px-3 py-2 align-top text-slate-600">{rule.scope}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

function ListaCard({ lista, variant = 'inclusion' }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { count: c } = await supabase
        .from('crm_list_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('list_id', lista.id);
      if (mounted) setCount(c);
    })();
    return () => { mounted = false; };
  }, [lista.id]);

  return (
    <div
      className={`border rounded-lg p-3 hover:shadow-md transition-shadow ${
        variant === 'antilist'
          ? 'border-red-200 bg-red-50'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex justify-between items-start mb-1.5 gap-2">
        <h3 className="font-semibold text-sm text-slate-900 truncate" title={lista.nome}>
          {lista.nome}
        </h3>
        <Badge variant={variant === 'antilist' ? 'red' : 'blue'}>{lista.list_type}</Badge>
      </div>
      {lista.description && (
        <p className="text-[11px] text-slate-600 mb-2 line-clamp-2">{lista.description}</p>
      )}
      <div className="flex justify-between items-center text-[10px] text-slate-500">
        <span className="flex items-center gap-1">
          <UsersIcon size={10} /> {count == null ? '…' : `${count} membros`}
        </span>
        <span className="uppercase tracking-wide">{lista.scope}</span>
      </div>
    </div>
  );
}

function Badge({ variant = 'gray', children }) {
  const colors = {
    gray:  'bg-slate-100 text-slate-700',
    blue:  'bg-blue-100 text-blue-700',
    red:   'bg-red-100 text-red-700',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${colors[variant] || colors.gray}`}>
      {children}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const map = {
    block: 'red',
    warn: 'amber',
    require_approval: 'blue',
  };
  return <Badge variant={map[severity] || 'gray'}>{severity}</Badge>;
}

function StatusBadge({ status }) {
  const map = {
    active: 'green',
    placeholder: 'amber',
    deprecated: 'gray',
  };
  return <Badge variant={map[status] || 'gray'}>{status}</Badge>;
}

function ListaCreateModal({ listType, onClose, onCreated }) {
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [entityType, setEntityType] = useState('mixed');
  const [scope, setScope] = useState('user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const autoSlug = (n) =>
    n.toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

  const handleSubmit = async () => {
    if (!nome) return;
    setSubmitting(true);
    setError(null);
    const { error: insertErr } = await supabase.from('crm_lists').insert({
      nome,
      slug: slug || autoSlug(nome),
      list_type: listType,
      entity_type: entityType,
      scope,
      description,
      is_active: true,
      created_by: 'ui_create',
    });
    setSubmitting(false);
    if (insertErr) {
      setError(insertErr.message);
      return;
    }
    onCreated();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10020] p-4">
      <div className="bg-white rounded-lg p-5 max-w-md w-full shadow-xl">
        <h3 className="text-base font-semibold mb-3 text-slate-900">
          Nova {listType === 'antilist' ? 'anti-lista' : 'lista'}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Nome *</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => {
                setNome(e.target.value);
                if (!slug) setSlug(autoSlug(e.target.value));
              }}
              className="w-full px-2.5 py-2 border border-slate-300 rounded text-xs"
              placeholder={listType === 'antilist' ? 'Ex: Concorrentes locais SP' : 'Ex: Decisores ICP Q2'}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-2.5 py-2 border border-slate-300 rounded text-xs font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Descrição</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-2.5 py-2 border border-slate-300 rounded text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1 text-slate-700">Entidade</label>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full px-2.5 py-2 border border-slate-300 rounded text-xs"
              >
                <option value="mixed">Mixed</option>
                <option value="contact">Contact</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-slate-700">Escopo</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="w-full px-2.5 py-2 border border-slate-300 rounded text-xs"
              >
                <option value="user">User (pessoal)</option>
                <option value="vertical">Vertical</option>
                <option value="global">Global</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 rounded"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!nome || submitting}
            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
}
